<template>
  <div class="acme-cert-container">
    <!-- 证书配置列表卡片 -->
    <div class="cert-card">
      <div class="card-header">
        <div class="card-title-wrapper">
          <h3 class="card-title">{{ $t('acmeMgmt.title') }}</h3>
          <span class="info-tag">{{ $t('acmeMgmt.certMgmt') }}</span>
        </div>
        <div class="card-actions">
          <el-button type="info" size="small" @click="showLogDialog">
            <el-icon><Document /></el-icon>
            {{ $t('acmeMgmt.viewLogs') }}
          </el-button>
          <el-button type="primary" size="small" @click="showCertEditDialog(null)">
            <el-icon><Plus /></el-icon>
            {{ $t('acmeMgmt.addConfig') }}
          </el-button>
        </div>
      </div>

      <div class="card-body">
        <div v-if="certList.length === 0" class="empty-state">
          <el-icon><InfoFilled /></el-icon>
          <p>{{ $t('acmeMgmt.noCerts') }}</p>
        </div>

        <div v-else class="table-responsive">
          <table class="cert-table">
            <thead>
              <tr>
                <th>{{ $t('acmeMgmt.name') }}</th>
                <th>{{ $t('acmeMgmt.mainDomain') }}</th>
                <th>{{ $t('acmeMgmt.subdomains') }}</th>
                <th>{{ $t('acmeMgmt.provider') }}</th>
                <th>{{ $t('acmeMgmt.challengeMethod') }}</th>
                <th>{{ $t('acmeMgmt.autoRenew') }}</th>
                <th>{{ $t('acmeMgmt.certStatus') }}</th>
                <th>{{ $t('acmeMgmt.actions') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in certList" :key="row.name">
                <td :data-label="$t('acmeMgmt.name')">{{ row.name }}</td>
                <td :data-label="$t('acmeMgmt.mainDomain')">{{ row.domain }}</td>
                <td :data-label="$t('acmeMgmt.subdomains')">{{ formatSubdomains(row.subdomains) }}</td>
                <td :data-label="$t('acmeMgmt.provider')">
                  <span class="provider-tag" :class="row.provider">
                    {{ row.provider === 'aliyun' ? $t('acmeMgmt.aliyun') : $t('acmeMgmt.tencent') }}
                  </span>
                </td>
                <td :data-label="$t('acmeMgmt.challengeMethod')">{{ row.challengeType === 'dns' ? $t('acmeMgmt.dnsChallenge') : row.challengeType }}</td>
                <td :data-label="$t('acmeMgmt.autoRenew')">
                  <div class="switch-wrapper">
                    <el-switch
                      v-model="row.autoRequest"
                      :active-value="true"
                      :inactive-value="false"
                      @change="handleAutoRequestChange(row)"
                      size="small"
                    />
                  </div>
                </td>
                <td :data-label="$t('acmeMgmt.certStatus')">
                  <span v-if="row.status" class="status-text">{{ row.status }}</span>
                  <span v-else class="status-badge danger">{{ $t('acmeMgmt.notApplied') }}</span>
                </td>
                <td :data-label="$t('acmeMgmt.actions')" class="action-cell">
                  <div class="action-buttons">
                    <el-button
                      type="success"
                      size="small"
                      @click="requestCertificate(row)"
                      :loading="requestingName === row.name"
                    >
                      {{ $t('acmeMgmt.apply') }}
                    </el-button>
                    <el-button
                      type="primary"
                      size="small"
                      @click="showCertEditDialog(row)"
                    >
                      {{ $t('acmeMgmt.edit') }}
                    </el-button>
                    <el-button
                      type="danger"
                      size="small"
                      @click="deleteCertificateConfig(row)"
                    >
                      {{ $t('acmeMgmt.delete') }}
                    </el-button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- 证书配置编辑对话框 -->
    <el-dialog
      v-model="certDialogVisible"
      :title="certDialogMode === 'edit' ? $t('acmeMgmt.editCertConfig') : $t('acmeMgmt.addCertConfig')"
      :width="isMobile ? '95%' : '600px'"
      :close-on-click-modal="false"
      :destroy-on-close="true"
      class="settings-dialog"
    >
      <el-form
        :model="currentRecord"
        :rules="recordRules"
        ref="recordFormRef"
        label-position="top"
      >
        <el-form-item :label="$t('acmeMgmt.configName')" prop="name">
          <el-input
            v-model="currentRecord.name"
            :disabled="certDialogMode === 'edit'"
            :placeholder="$t('acmeMgmt.inputConfigName')"
          />
        </el-form-item>

        <el-form-item :label="$t('acmeMgmt.mainDomainLabel')" prop="domain">
          <el-input v-model="currentRecord.domain" :disabled="certDialogMode === 'edit'" :placeholder="$t('acmeMgmt.inputMainDomain')" />
        </el-form-item>

        <el-form-item :label="$t('acmeMgmt.subdomainsLabel')" prop="subdomains">
          <el-select
            v-model="currentRecord.subdomains"
            multiple
            allow-create
            filterable
            default-first-option
            :placeholder="$t('acmeMgmt.inputSubdomains')"
            style="width: 100%"
          >
            <el-option
              v-for="item in currentRecord.subdomains"
              :key="item"
              :label="item"
              :value="item"
            />
          </el-select>
        </el-form-item>

        <el-form-item :label="$t('acmeMgmt.dnsProvider')" prop="provider">
          <el-select v-model="currentRecord.provider" style="width: 100%">
            <el-option :label="$t('acmeMgmt.aliyun')" value="aliyun" />
            <el-option :label="$t('acmeMgmt.tencent')" value="tencent" />
          </el-select>
        </el-form-item>

        <el-form-item :label="$t('acmeMgmt.challengeType')" prop="challengeType">
          <el-select v-model="currentRecord.challengeType" style="width: 100%">
            <el-option :label="$t('acmeMgmt.dnsChallenge')" value="dns" />
          </el-select>
        </el-form-item>

        <el-form-item :label="$t('acmeMgmt.autoRenewal')" prop="autoRequest">
          <div class="auto-renew-wrapper">
            <el-switch
              v-model="currentRecord.autoRequest"
              :active-value="true"
              :inactive-value="false"
            />
            <span class="switch-label" :class="{ 'enabled': currentRecord.autoRequest }">
              {{ currentRecord.autoRequest ? $t('acmeMgmt.enabled') : $t('acmeMgmt.disabled') }}
            </span>
            <span class="hint-text">{{ $t('acmeMgmt.hintAutoRenew') }}</span>
          </div>
        </el-form-item>
      </el-form>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="certDialogVisible = false">{{ $t('acmeMgmt.cancel') }}</el-button>
          <el-button
            type="primary"
            @click="saveCertificateConfig"
            :loading="recordLoading"
          >
            {{ certDialogMode === 'edit' ? $t('acmeMgmt.update') : $t('acmeMgmt.save') }}
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 强制申请证书确认对话框 -->
    <el-dialog
      v-model="forceRequestDialogVisible"
      :title="$t('acmeMgmt.forceRequestTitle')"
      :width="isMobile ? '95%' : '500px'"
      :close-on-click-modal="false"
      :destroy-on-close="true"
      class="settings-dialog"
    >
      <div class="force-request-content">
        <el-alert
          type="warning"
          :closable="false"
          show-icon
        >
          <p>{{ $t('acmeMgmt.forceRequestWarning') }}</p>
          <p class="alert-hint">{{ $t('acmeMgmt.forceRequestHint') }}</p>
        </el-alert>
      </div>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="forceRequestDialogVisible = false">{{ $t('acmeMgmt.cancel') }}</el-button>
          <el-button
            type="primary"
            @click="confirmForceRequest"
            :loading="requestingName === forceRequestName"
          >
            {{ $t('acmeMgmt.forceApply') }}
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- ACME日志查看对话框 -->
    <el-dialog
      v-model="logDialogVisible"
      :title="$t('acmeMgmt.logTitle')"
      :width="isMobile ? '95%' : '900px'"
      :close-on-click-modal="false"
      :destroy-on-close="true"
      class="settings-dialog log-dialog"
    >
      <div class="log-toolbar">
        <div class="log-stats">
          <span>{{ $t('acmeMgmt.totalLogs', { count: logTotal }) }}</span>
          <div class="auto-refresh-control">
            <span class="refresh-label">{{ $t('acmeMgmt.autoRefresh') }}</span>
            <el-switch
              v-model="autoRefreshEnabled"
              :active-value="true"
              :inactive-value="false"
              @change="toggleAutoRefresh"
              size="small"
              active-color="#409eff"
            />
            <span class="refresh-interval" v-if="autoRefreshEnabled">{{ $t('acmeMgmt.refreshInterval') }}</span>
          </div>
        </div>
        <div class="log-actions">
          <el-button
            type="success"
            size="small"
            @click="downloadAllLogs"
            :loading="logDownloading"
            :icon="Download"
          >
            {{ $t('acmeMgmt.downloadAllLogs') }}
          </el-button>
          <el-button
            type="primary"
            size="small"
            @click="refreshLogs"
            :loading="logLoading"
            :icon="Refresh"
          >
            {{ $t('acmeMgmt.refreshNow') }}
          </el-button>
          <el-button
            type="danger"
            size="small"
            @click="clearLogs"
            :loading="logClearing"
          >
            {{ $t('acmeMgmt.clearLogs') }}
          </el-button>
        </div>
      </div>

      <div class="log-content" ref="logContentRef">
        <div v-if="logsData.length === 0" class="empty-log">
          <el-icon><InfoFilled /></el-icon>
          <p>{{ $t('acmeMgmt.noLogs') }}</p>
        </div>
        <div v-else class="log-list">
          <div
            v-for="(log, index) in logsData"
            :key="index"
            class="log-item"
            :class="`log-level-${log.Level.toLowerCase()}`"
          >
            <span class="log-time">{{ log.Time }}</span>
            <span class="log-level" :class="`level-${log.Level.toLowerCase()}`">
              [{{ log.Level }}]
            </span>
            <span class="log-message">{{ log.Content }}</span>
          </div>
        </div>
      </div>

      <div class="log-pagination" v-if="logTotal > 0">
        <el-pagination
          v-model:current-page="logCurrentPage"
          v-model:page-size="logPageSize"
          :page-sizes="[20, 50, 100, 200]"
          :total="logTotal"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleLogSizeChange"
          @current-change="handleLogPageChange"
          small
        />
      </div>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="logDialogVisible = false">{{ $t('acmeMgmt.close') }}</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, InfoFilled, Document, Refresh, Download } from '@element-plus/icons-vue'
import { API_BASE_URL } from "@/config"
import { useRouter } from 'vue-router'

const router = useRouter()
const isMobile = ref(false)

// 检测移动端
const checkMobile = () => {
  isMobile.value = window.innerWidth < 768
}

// 证书配置列表
const certList = ref([])
const refreshInterval = ref(null)
const logRefreshInterval = ref(null)

// 当前正在申请证书的名称
const requestingName = ref('')

// 证书配置编辑对话框相关
const certDialogVisible = ref(false)
const certDialogMode = ref('add') // 'add' or 'edit'
const currentRecord = ref({
  name: '',
  domain: '',
  subdomains: [],
  provider: 'aliyun',
  challengeType: 'dns',
  autoRequest: true // 默认开启自动续期
})
const recordFormRef = ref(null)
const recordLoading = ref(false)

// 强制申请证书对话框
const forceRequestDialogVisible = ref(false)
const forceRequestName = ref('')

// 日志对话框相关
const logDialogVisible = ref(false)
const logLoading = ref(false)
const logClearing = ref(false)
const logDownloading = ref(false)
const logsData = ref([])
const logTotal = ref(0)
const logCurrentPage = ref(1)
const logPageSize = ref(50)
const logContentRef = ref(null)
const autoRefreshEnabled = ref(true) // 默认开启自动刷新

// 格式化子域名显示
const formatSubdomains = (subdomains) => {
  if (!subdomains || subdomains.length === 0) return '-'
  if (subdomains.length > 2) {
    return `${subdomains.slice(0, 2).join(', ')} 等${subdomains.length}个`
  }
  return subdomains.join(', ')
}

// 记录验证规则
const recordRules = {
  name: [
    { required: true, message: '请输入配置名称', trigger: 'blur' },
    { pattern: /^[a-zA-Z0-9_-]{1,64}$/, message: '名称只能包含字母、数字、下划线和横线', trigger: 'blur' }
  ],
  domain: [
    { required: true, message: '请输入主域名', trigger: 'blur' },
    { pattern: /^[a-zA-Z0-9.-]{1,255}$/, message: '请输入有效的域名', trigger: 'blur' }
  ],
  subdomains: [
    { required: true, message: '请至少添加一个子域名', trigger: 'change' }
  ],
  provider: [
    { required: true, message: '请选择DNS提供商', trigger: 'change' }
  ],
  challengeType: [
    { required: true, message: '请选择验证方式', trigger: 'change' }
  ]
}

// 检查登录状态
const checkLogin = (data) => {
  if (data?.errCode === 1000) {
    ElMessage.warning('请先登录系统')
    router.push('/user-login?callback=acme-mgmt')
    return false
  }
  return true
}

// 处理自动续期开关变更
const handleAutoRequestChange = async (row) => {
  try {
    const params = new URLSearchParams()
    params.append('name', row.name)
    params.append('domain', row.domain)

    row.subdomains.forEach(item => {
        params.append('subdomains', item)
    })

    params.append('provider', row.provider)
    params.append('challengeType', row.challengeType)
    params.append('autoRequest', row.autoRequest)
    
    const response = await fetch(`${API_BASE_URL}/update-acme-config`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      credentials: 'include',
      body: params
    })
    
    if (!response.ok) throw new Error('网络请求失败')
    
    const data = await response.json()
    if (!checkLogin(data)) return
    
    if (data.errCode !== 0) {
      throw new Error(data.errMsg || '更新失败')
    }
    
    ElMessage.success(`自动续期已${row.autoRequest ? '开启' : '关闭'}`)
  } catch (error) {
    console.error('更新自动续期状态失败:', error)
    ElMessage.error(`更新失败: ${error.message || '未知错误'}`)
    // 恢复开关状态
    row.autoRequest = !row.autoRequest
  }
}

// 显示证书配置编辑对话框
const showCertEditDialog = (record) => {
  if (record) {
    // 编辑现有配置
    certDialogMode.value = 'edit'
    currentRecord.value = {
      ...record,
      subdomains: record.subdomains || []
    }
  } else {
    // 新增配置 - 自动续期默认开启
    certDialogMode.value = 'add'
    currentRecord.value = {
      name: '',
      domain: '',
      subdomains: [],
      provider: 'aliyun',
      challengeType: 'dns',
      autoRequest: true // 确保新增时默认开启
    }
  }
  certDialogVisible.value = true
}

// 保存证书配置
const saveCertificateConfig = async () => {
  try {
    await recordFormRef.value.validate()
    recordLoading.value = true
    
    const params = new URLSearchParams()
    params.append('name', currentRecord.value.name)
    params.append('domain', currentRecord.value.domain)

    currentRecord.value.subdomains.forEach(item => {
        params.append('subdomains', item)
    })

    params.append('provider', currentRecord.value.provider)
    params.append('challengeType', currentRecord.value.challengeType)
    params.append('autoRequest', currentRecord.value.autoRequest)
    
    const endpoint = (certDialogMode.value === 'edit') ? '/update-acme-config' : '/add-acme-config'
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      credentials: 'include',
      body: params
    })
    
    if (!response.ok) throw new Error('网络请求失败')
    
    const data = await response.json()
    if (!checkLogin(data)) return
    
    if (data.errCode !== 0) {
      throw new Error(data.errMsg || '操作失败')
    }
    
    ElMessage.success(`证书配置${certDialogMode.value === 'edit' ? '更新' : '新增'}成功`)
    certDialogVisible.value = false
    fetchData()
  } catch (error) {
    console.error('保存证书配置失败:', error)
    ElMessage.error(`保存失败: ${error.message || '未知错误'}`)
  } finally {
    recordLoading.value = false
  }
}

// 删除证书配置
const deleteCertificateConfig = async (row) => {
  try {
    await ElMessageBox.confirm(`确定要删除证书配置 "${row.name}" 吗？`, '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    
    const params = new URLSearchParams()
    params.append('name', row.name)
    
    const response = await fetch(`${API_BASE_URL}/delete-acme-config?${params.toString()}`, {
      method: 'DELETE',
      credentials: 'include'
    })
    
    if (!response.ok) throw new Error('网络请求失败')
    
    const data = await response.json()
    if (!checkLogin(data)) return
    
    if (data.errCode !== 0) {
      throw new Error(data.errMsg || '删除失败')
    }
    
    ElMessage.success('证书配置删除成功')
    fetchData()
  } catch (error) {
    if (error !== 'cancel') {
      console.error('删除证书配置失败:', error)
      ElMessage.error(`删除失败: ${error.message || '未知错误'}`)
    }
  }
}

// 申请证书
const requestCertificate = async (row) => {
  try {
    // 检查证书是否即将过期
    if (!row.deadtime) {
        forceRequestName.value = row.name
        forceRequestDialogVisible.value = true
        return
    }
    
    await submitCertificateRequest(row.name)
  } catch (error) {
    console.error('申请证书失败:', error)
  }
}

// 确认强制申请证书
const confirmForceRequest = async () => {
  forceRequestDialogVisible.value = false
  await submitCertificateRequest(forceRequestName.value)
  forceRequestName.value = ''
}

// 提交证书申请请求
const submitCertificateRequest = async (name) => {
  try {
    requestingName.value = name
    
    const params = new URLSearchParams()
    params.append('name', name)
    
    const response = await fetch(`${API_BASE_URL}/request-acme-cert`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      credentials: 'include',
      body: params
    })
    
    if (!response.ok) throw new Error('网络请求失败')
    
    const data = await response.json()
    if (!checkLogin(data)) return
    
    if (data.errCode !== 0) {
      throw new Error(data.errMsg || '申请失败')
    }
    
    ElMessage.success('证书申请任务已提交，请稍后查看状态')
    
    // 延迟一下再刷新，让后端有时间处理
    setTimeout(() => {
      fetchData()
    }, 2000)
  } catch (error) {
    console.error('申请证书失败:', error)
    ElMessage.error(`申请失败: ${error.message || '未知错误'}`)
  } finally {
    requestingName.value = ''
  }
}

// 显示日志对话框
const showLogDialog = () => {
  logDialogVisible.value = true
  fetchLogs()
  // 开启日志自动刷新（如果开启状态）
  if (autoRefreshEnabled.value) {
    startLogAutoRefresh()
  }
}

// 切换自动刷新
const toggleAutoRefresh = (enabled) => {
  if (enabled) {
    startLogAutoRefresh()
    ElMessage.success('已开启自动刷新（5秒/次）')
  } else {
    stopLogAutoRefresh()
    ElMessage.info('已关闭自动刷新')
  }
}

// 启动日志自动刷新
const startLogAutoRefresh = () => {
  stopLogAutoRefresh() // 先停止已有的定时器
  logRefreshInterval.value = setInterval(() => {
    if (logDialogVisible.value) {
      fetchLogs(false) // 静默刷新，不显示加载状态
    }
  }, 5000)
}

// 停止日志自动刷新
const stopLogAutoRefresh = () => {
  if (logRefreshInterval.value) {
    clearInterval(logRefreshInterval.value)
    logRefreshInterval.value = null
  }
}

// 获取日志
const fetchLogs = async (showLoading = true) => {
  try {
    if (showLoading) {
      logLoading.value = true
    }
    
    const params = new URLSearchParams({
      module: 'acme',
      page: logCurrentPage.value,
      size: logPageSize.value
    })
    
    const response = await fetch(`${API_BASE_URL}/get-moduleLog?${params.toString()}`, {
      credentials: 'include'
    })
    
    if (!response.ok) throw new Error('网络请求失败')
    
    const data = await response.json()
    if (!checkLogin(data)) return
    
    if (data.errCode !== 0) {
      throw new Error(data.errMsg || '获取日志失败')
    }
    
    logsData.value = data.data || []
    logTotal.value = data.count || 0
    
    // 只有在非自动刷新（即手动操作）时才滚动到顶部
    if (showLoading && logContentRef.value) {
      logContentRef.value.scrollTop = 0
    }
  } catch (error) {
    console.error('获取日志失败:', error)
    if (showLoading) {
      ElMessage.error(`获取日志失败: ${error.message || '未知错误'}`)
    }
  } finally {
    if (showLoading) {
      logLoading.value = false
    }
  }
}

// 刷新日志
const refreshLogs = () => {
  fetchLogs(true)
}

// 清空日志
const clearLogs = async () => {
  try {
    await ElMessageBox.confirm('确定要清空所有ACME日志吗？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    
    logClearing.value = true
    
    const params = new URLSearchParams({
      module: 'acme'
    })
    
    const response = await fetch(`${API_BASE_URL}/clear-moduleLog?${params.toString()}`, {
      method: 'DELETE',
      credentials: 'include'
    })
    
    if (!response.ok) throw new Error('网络请求失败')
    
    const data = await response.json()
    if (!checkLogin(data)) return
    
    if (data.errCode !== 0) {
      throw new Error(data.errMsg || '清空日志失败')
    }
    
    ElMessage.success('日志已清空')
    fetchLogs(true)
  } catch (error) {
    if (error !== 'cancel') {
      console.error('清空日志失败:', error)
      ElMessage.error(`清空日志失败: ${error.message || '未知错误'}`)
    }
  } finally {
    logClearing.value = false
  }
}

// 下载所有日志
const downloadAllLogs = async () => {
  try {
    logDownloading.value = true
    
    // 获取所有日志（设置一个足够大的size值）
    const params = new URLSearchParams({
      module: 'acme',
      page: 1,
      size: 10000 // 设置一个较大的数值，获取尽可能多的日志
    })
    
    const response = await fetch(`${API_BASE_URL}/get-moduleLog?${params.toString()}`, {
      credentials: 'include'
    })
    
    if (!response.ok) throw new Error('网络请求失败')
    
    const data = await response.json()
    if (!checkLogin(data)) return
    
    if (data.errCode !== 0) {
      throw new Error(data.errMsg || '获取日志失败')
    }
    
    const allLogs = data.data || []
    
    if (allLogs.length === 0) {
      ElMessage.warning('暂无日志可下载')
      return
    }
    
    // 格式化所有日志内容
    let logContent = ''
    allLogs.forEach(log => {
      logContent += `[${log.Time}] [${log.Level}] ${log.Content}\n`
    })
    
    // 创建下载链接
    const blob = new Blob([logContent], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `acme_all_logs_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.log`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    
    ElMessage.success(`所有日志下载成功，共 ${allLogs.length} 条记录`)
  } catch (error) {
    console.error('下载日志失败:', error)
    ElMessage.error(`下载日志失败: ${error.message || '未知错误'}`)
  } finally {
    logDownloading.value = false
  }
}

// 处理每页条数变化
const handleLogSizeChange = (size) => {
  logPageSize.value = size
  logCurrentPage.value = 1
  fetchLogs(true)
}

// 处理页码变化
const handleLogPageChange = (page) => {
  logCurrentPage.value = page
  fetchLogs(true)
}

// 设置自动刷新
const setupAutoRefresh = () => {
  // 每5秒自动刷新一次数据
  refreshInterval.value = setInterval(fetchData, 5000)
}

// 清除自动刷新
const clearAutoRefresh = () => {
  if (refreshInterval.value) {
    clearInterval(refreshInterval.value)
    refreshInterval.value = null
  }
}

// 获取数据
const fetchData = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/get-acme-configs`, {
      credentials: 'include'
    })
    
    if (!response.ok) throw new Error('网络请求失败')
    
    const data = await response.json()
    if (!checkLogin(data)) return
    
    if (data.errCode !== 0) {
      throw new Error(data.errMsg || '获取数据失败')
    }
    
    // 更新证书配置列表
    certList.value = data.data || []
  } catch (error) {
    console.error('获取数据失败:', error)
    ElMessage.error(`获取数据失败: ${error.message || '未知错误'}`)
  }
}

// 窗口大小变化处理
const handleResize = () => {
  checkMobile()
}

// 初始化
onMounted(() => {
  checkMobile()
  window.addEventListener('resize', handleResize)
  fetchData()
  setupAutoRefresh()
})

// 组件卸载时清除定时器
onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  clearAutoRefresh()
  stopLogAutoRefresh()
})
</script>

<style scoped lang="scss">
.acme-cert-container {
  padding: 0px;
  max-width: 100%;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 0 12px;
  }
  
  @media (max-width: 480px) {
    padding: 0 8px;
  }
}

.cert-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.08);
  margin-bottom: 24px;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
  
  &:hover {
    box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.12);
  }
  
  .card-header {
    height: 60px;
    padding: 16px 24px;
    border-bottom: 1px solid #ebeef5;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: #f9fafc;
    
    .card-title-wrapper {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;
      
      .card-title {
        font-size: 16px;
        font-weight: 600;
        color: #303133;
        margin: 0;
      }
    }
    
    .card-actions {
      display: flex;
      gap: 8px;
    }
  }
  
  .card-body {
    padding: 24px;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    .card-header {
      padding: 12px 16px;
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
      
      .card-title-wrapper {
        width: 100%;
      }
      
      .card-actions {
        width: 100%;
        justify-content: flex-end;
      }
    }
    
    .card-body {
      padding: 16px;
    }
  }
  
  @media (max-width: 480px) {
    .card-header {
      padding: 10px 12px;
      
      .card-title-wrapper {
        .card-title {
          font-size: 15px;
        }
      }
    }
    
    .card-body {
      padding: 12px;
    }
  }
}

.info-tag {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  background-color: #ecf5ff;
  color: #409eff;
}

.table-responsive {
  overflow-x: auto;
  width: 100%;
  
  @media (max-width: 768px) {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
}

.cert-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  
  th, td {
    padding: 12px 8px;
    text-align: left;
    border-bottom: 1px solid #ebeef5;
  }
  
  th {
    background-color: #f5f7fa;
    color: #909399;
    font-weight: 600;
    white-space: nowrap;
  }
  
  td {
    color: #606266;
    vertical-align: middle;
  }
  
  tr:hover td {
    background-color: #f5f7fa;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    min-width: 100%;
    display: block;
    
    thead {
      display: none;
    }
    
    tbody, tr {
      width: 100%;
      display: block;
    }
    
    tr {
      margin-bottom: 16px;
      border: 1px solid #ebeef5;
      border-radius: 4px;
      background: #fff;
    }
    
    td {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 10px 12px;
      border-bottom: 1px solid #ebeef5;
      width: 100%;
      box-sizing: border-box;
      
      &:before {
        content: attr(data-label);
        font-weight: 600;
        color: #909399;
        margin-right: 12px;
        flex-shrink: 0;
        min-width: 80px;
        font-size: 13px;
      }
      
      &:last-child {
        border-bottom: none;
      }
      
      &.action-cell {
        padding: 10px 12px;
        
        &:before {
          align-self: flex-start;
          padding-top: 4px;
        }
      }
    }
  }
  
  @media (max-width: 480px) {
    td {
      padding: 8px 10px;
      font-size: 12px;
      
      &:before {
        min-width: 70px;
        font-size: 12px;
      }
    }
  }
}

.provider-tag {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  
  &.aliyun {
    background-color: #f0f9eb;
    color: #67c23a;
  }
  
  &.tencent {
    background-color: #ecf5ff;
    color: #409eff;
  }
}

.status-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  
  &.danger {
    background-color: #fef0f0;
    color: #f56c6c;
  }
}

.status-text {
  font-size: 12px;
  color: #606266;
}

.switch-wrapper {
  display: inline-block;
  
  :deep(.el-switch) {
    .el-switch__core {
      width: 36px !important;
    }
  }
}

.action-cell {
  .action-buttons {
    display: flex;
    gap: 4px;
    flex-wrap: nowrap;
    
    .el-button {
      flex-shrink: 0;
    }
    
    .el-button + .el-button {
      margin-left: 0;
    }
  }
  
  @media (max-width: 768px) {
    .action-buttons {
      justify-content: flex-end;
    }
    
    .el-button--small {
      padding: 4px 8px;
      font-size: 12px;
    }
  }
  
  @media (max-width: 480px) {
    .action-buttons {
      gap: 2px;
      
      .el-button--small {
        padding: 4px 6px;
        font-size: 11px;
      }
    }
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #909399;
  
  .el-icon {
    font-size: 32px;
    margin-bottom: 12px;
  }
  
  p {
    margin: 0;
    font-size: 14px;
  }
}

.auto-renew-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  
  .switch-label {
    font-size: 14px;
    
    &.enabled {
      color: #67c23a;
    }
    
    &:not(.enabled) {
      color: #909399;
    }
  }
  
  .hint-text {
    color: #909399;
    font-size: 13px;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    
    .hint-text {
      font-size: 12px;
    }
  }
}

.force-request-content {
  padding: 8px;
  
  .alert-hint {
    font-size: 12px;
    margin-top: 8px;
    color: #e6a23c;
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  
  @media (max-width: 480px) {
    flex-direction: column;
    
    .el-button {
      width: 100%;
      margin-left: 0 !important;
    }
  }
}

/* 日志对话框样式 */
.log-dialog {
  :deep(.el-dialog__body) {
    padding: 20px;
    max-height: 70vh;
    overflow-y: auto;
  }
}

.log-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #ebeef5;
  
  .log-stats {
    display: flex;
    align-items: center;
    gap: 24px;
    font-size: 13px;
    color: #606266;
    
    .auto-refresh-control {
      display: flex;
      align-items: center;
      gap: 8px;
      padding-left: 16px;
      border-left: 1px solid #dcdfe6;
      
      .refresh-label {
        font-size: 12px;
        color: #909399;
      }
      
      .refresh-interval {
        font-size: 12px;
        color: #67c23a;
      }
    }
  }
  
  .log-actions {
    display: flex;
    gap: 8px;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
    
    .log-stats {
      width: 100%;
      flex-wrap: wrap;
      gap: 12px;
      
      .auto-refresh-control {
        padding-left: 0;
        border-left: none;
      }
    }
    
    .log-actions {
      width: 100%;
      justify-content: flex-end;
    }
  }
  
  @media (max-width: 480px) {
    .log-stats {
      font-size: 12px;
    }
  }
}

.log-content {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background-color: #fafafa;
  margin-bottom: 16px;
  max-height: 400px;
  overflow-y: auto;
  
  .empty-log {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px;
    color: #909399;
    
    .el-icon {
      font-size: 32px;
      margin-bottom: 12px;
    }
    
    p {
      margin: 0;
      font-size: 14px;
    }
  }
  
  .log-list {
    padding: 12px;
    
    .log-item {
      padding: 8px 12px;
      border-bottom: 1px solid #e4e7ed;
      font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
      font-size: 13px;
      line-height: 1.5;
      display: flex;
      align-items: flex-start;
      gap: 8px;
      
      &:last-child {
        border-bottom: none;
      }
      
      &.log-level-error {
        background-color: #fef0f0;
      }
      
      &.log-level-warn {
        background-color: #fdf6ec;
      }
      
      .log-time {
        color: #909399;
        font-size: 12px;
        white-space: nowrap;
        flex-shrink: 0;
      }
      
      .log-level {
        font-weight: 600;
        font-size: 12px;
        white-space: nowrap;
        flex-shrink: 0;
        
        &.level-error {
          color: #f56c6c;
        }
        
        &.level-warn {
          color: #e6a23c;
        }
        
        &.level-info {
          color: #409eff;
        }
      }
      
      .log-message {
        color: #303133;
        word-break: break-all;
        flex: 1;
      }
    }
  }
  
  @media (max-width: 768px) {
    max-height: 350px;
    
    .log-list {
      padding: 8px;
      
      .log-item {
        font-size: 12px;
        flex-wrap: wrap;
        gap: 4px;
        
        .log-time {
          width: 100%;
          margin-bottom: 2px;
        }
        
        .log-message {
          width: 100%;
          margin-left: 0;
        }
      }
    }
  }
  
  @media (max-width: 480px) {
    max-height: 300px;
    
    .log-list {
      .log-item {
        font-size: 11px;
      }
    }
  }
}

.log-pagination {
  display: flex;
  justify-content: flex-end;
  
  @media (max-width: 768px) {
    justify-content: center;
  }
}

/* Element Plus 对话框移动端适配 */
.settings-dialog {
  :deep(.el-dialog) {
    @media (max-width: 768px) {
      width: 95% !important;
      margin-top: 5vh !important;
    }
    
    @media (max-width: 480px) {
      width: 100% !important;
      height: auto !important;
      margin: 0 !important;
      max-height: 100%;
      border-radius: 0;
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      margin-bottom: 0 !important;
      
      .el-dialog__header {
        padding: 12px 16px;
        border-bottom: 1px solid #ebeef5;
      }
      
      .el-dialog__body {
        padding: 16px;
        max-height: calc(80vh - 120px);
        overflow-y: auto;
      }
      
      .el-dialog__footer {
        padding: 12px 16px;
        border-top: 1px solid #ebeef5;
      }
    }
  }
}

/* Element Plus 组件适配 */
:deep(.el-input__inner) {
  @media (max-width: 768px) {
    font-size: 13px;
    height: 32px;
    line-height: 32px;
  }
  
  @media (max-width: 480px) {
    font-size: 12px;
    height: 30px;
    line-height: 30px;
  }
}

:deep(.el-select) {
  @media (max-width: 768px) {
    .el-input__inner {
      height: 32px;
      line-height: 32px;
    }
  }
  
  @media (max-width: 480px) {
    .el-input__inner {
      height: 30px;
      line-height: 30px;
    }
  }
}

:deep(.el-form-item__label) {
  @media (max-width: 768px) {
    font-size: 13px;
    padding-bottom: 4px;
  }
  
  @media (max-width: 480px) {
    font-size: 12px;
  }
}

:deep(.el-button--small) {
  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 5px 8px;
    font-size: 11px;
  }
}

:deep(.el-switch) {
  @media (max-width: 768px) {
    .el-switch__core {
      width: 36px !important;
      height: 18px !important;
      
      &:after {
        width: 14px !important;
        height: 14px !important;
      }
    }
  }
}
</style>